import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Button, Icon } from 'rsuite';
import { map, reduce, get, getOr, isEmpty, includes } from 'lodash/fp';
import formatFullName from '../../../helpers/formatFullName';
import ColumnChanges from '../ColumnChanges';

const reduceWithKey = reduce.convert({ cap: false });

const Paragraph = styled.p`
  font-size: 15px;
  margin-top: 0;
`;

const excludedFields = ['status', 'donor_id', 'location_id'];

const ActionLabels = {
  view: 'Viewed',
  update: 'Edited',
  create: 'Created',
  delete: 'Deleted',
  login: 'Logged in',
  logout: 'Logged out',
  login_fail: 'Logged in failed'
};

const LogItem = ({ log, columns, ...props }) => {
  const { id, createdAt, objAction, member, ipAddress, changedColumns } = log;
  const [showDetails, setShowDetails] = useState(false);
  const hasChanges = !isEmpty(changedColumns);

  const changesColumnsToArray = () =>
    reduceWithKey(
      (all, newVal, key) => {
        const colSettings = get(key, columns);
        if (isEmpty(colSettings) || includes(key, excludedFields)) return all;

        const oldVal = getOr('', `old.${key}`, changedColumns);
        return [...all, { oldVal, newVal, colSettings }];
      },
      [],
      changedColumns.new
    );

  const handleShowDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <div {...props}>
      <Paragraph>{moment(createdAt).format('lll')}</Paragraph>
      <Paragraph>
        {ActionLabels[objAction]} by{' '}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`/manage/team/view/${member.id}`}
        >
          {formatFullName([member.firstname, member.lastname])}
        </a>
        &nbsp;with IP {ipAddress}
        &nbsp;
        {hasChanges && (
          <Button
            onClick={handleShowDetails}
            appearance="link"
            style={{ padding: 0 }}
          >
            {showDetails ? 'hide' : 'show'} details{' '}
            <Icon icon={showDetails ? 'arrow-up-line' : 'arrow-down-line'} />
          </Button>
        )}
      </Paragraph>
      {hasChanges &&
        showDetails &&
        map(
          ({ oldVal, newVal, colSettings }) => (
            <ColumnChanges
              key={`${id}-${get('name', colSettings)}`}
              action={objAction}
              oldVal={oldVal}
              newVal={newVal}
              colSettings={colSettings}
            />
          ),
          changesColumnsToArray(changedColumns)
        )}
    </div>
  );
};

const LogType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  snapId: PropTypes.number.isRequired,
  memberId: PropTypes.number.isRequired,
  locationId: PropTypes.number.isRequired,
  objId: PropTypes.number.isRequired,
  obj2Id: PropTypes.number,
  objType: PropTypes.string.isRequired,
  objAction: PropTypes.string.isRequired,
  ipAddress: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  changedColumns: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({})
  ]),
  createdAt: PropTypes.string.isRequired,
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string.isRequired
  })
});

LogItem.propTypes = {
  log: LogType.isRequired,
  columns: PropTypes.shape({}).isRequired
};

export default LogItem;
